import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Drum, MapPin, Megaphone, ChevronRight, X } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useLang } from "../i18n/LanguageContext";
import { ShareBanner } from "./ShareBanner";

const ROTATE_MS = 9000;
const KEY = "announce-dismissed";

function diff(dateStr) {
  const target = new Date(`${dateStr}T00:00:00`);
  const ms = Math.max(0, target - new Date());
  return { d: Math.floor(ms / 864e5), h: Math.floor(ms / 36e5) % 24, m: Math.floor(ms / 6e4) % 60, s: Math.floor(ms / 1e3) % 60 };
}
export const detailsPath = (b) => (b.event_id ? `/events/${b.event_id}` : `/events#announcement-${b.id || "legacy"}`);

function StickyBar({ data, t, tr, visible }) {
  return (
    <div className={`fixed inset-x-0 top-[72px] z-40 transition-transform duration-500 ${visible ? "translate-y-0" : "-translate-y-[150%]"}`} data-testid="odwira-sticky-bar" aria-hidden={!visible}>
      <Link to={detailsPath(data)} className="mx-auto flex max-w-7xl items-center gap-3 rounded-b-2xl border-x border-b border-gold/60 bg-crimson px-4 py-2 text-parchment shadow-[0_12px_30px_-10px_rgba(122,12,12,0.8)]" data-testid="odwira-sticky-link">
        <span className="announce-ribbon shrink-0"><Megaphone size={12} /> {tr(data.label || "Important Announcement")}</span>
        <span className="truncate font-serif-display text-base font-bold sm:text-lg">{data.title}</span>
        <span className="ml-auto hidden shrink-0 font-mono-accent text-sm font-bold text-gold sm:block" data-testid="odwira-sticky-timer">{t.d}d {String(t.h).padStart(2, "0")}h {String(t.m).padStart(2, "0")}m</span>
        <ChevronRight size={16} className="shrink-0 text-gold" />
      </Link>
    </div>
  );
}

function Dots({ count, index, onPick }) {
  if (count < 2) return null;
  return (
    <div className="flex items-center gap-2" data-testid="odwira-dots">
      {Array.from({ length: count }).map((_, i) => (
        <button key={`dot-${i}`} onClick={() => onPick(i)} aria-label={`Announcement ${i + 1}`} data-testid={`odwira-dot-${i}`}
          className={`h-2.5 rounded-full transition-all ${i === index ? "w-8 bg-gold-bright" : "w-2.5 bg-parchment/40 hover:bg-parchment/70"}`} />
      ))}
    </div>
  );
}

function DrumFab({ count, onOpen, tr }) {
  return (
    <button onClick={onOpen} className="fixed bottom-6 right-6 z-40 flex h-16 w-16 items-center justify-center rounded-full bg-gold text-crimson shadow-[0_0_0_8px_rgba(217,155,38,0.25),0_12px_30px_-8px_rgba(0,0,0,0.5)] transition-colors hover:bg-gold-bright" data-testid="announce-drum-fab" title={tr("Show announcements")}>
      <span className="announce-drum flex"><Drum size={30} /></span>
      <span className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-crimson font-mono-accent text-xs font-bold text-parchment" data-testid="announce-drum-count">{count}</span>
    </button>
  );
}

function useCountdown(date) {
  const [t, setT] = useState(null);
  useEffect(() => {
    if (!date) return undefined;
    setT(diff(date));
    const id = setInterval(() => setT(diff(date)), 1000);
    return () => clearInterval(id);
  }, [date]);
  return t;
}

function useBannerRotation(count) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (count < 2) return undefined;
    const id = setInterval(() => setIdx((i) => (i + 1) % count), ROTATE_MS);
    return () => clearInterval(id);
  }, [count]);
  return [idx, setIdx];
}

function useScrolled(threshold) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
}

function Timer({ t, tr }) {
  const cells = [["Days", t.d], ["Hours", t.h], ["Minutes", t.m], ["Seconds", t.s]];
  return (
    <div className="flex items-center gap-3" data-testid="odwira-timer">
      {cells.map(([l, v]) => (
        <div key={l} className="min-w-[78px] rounded-2xl border-2 border-gold/70 bg-black/20 px-3 py-3 text-center shadow-inner backdrop-blur">
          <div className="font-mono-accent text-4xl font-bold text-gold-bright sm:text-5xl">{String(v).padStart(2, "0")}</div>
          <div className="eyebrow text-[10px] text-parchment">{tr(l)}</div>
        </div>
      ))}
    </div>
  );
}

function BannerBody({ data, tr, count, idx, onPick }) {
  return (
    <div className="flex items-start gap-5">
      <div className="announce-drum flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-gold text-crimson shadow-[0_0_0_10px_rgba(217,155,38,0.18)]"><Drum size={38} /></div>
      <div className="min-w-0">
        <span className="announce-ribbon" data-testid="odwira-label"><Megaphone size={13} /> {tr(data.label || "Important Announcement")}</span>
        <h2 className="mt-3 font-serif-display text-4xl font-bold leading-[1.05] text-gold-bright drop-shadow-[0_2px_0_rgba(0,0,0,0.35)] sm:text-5xl lg:text-6xl" data-testid="odwira-title">{data.title}</h2>
        <p className="mt-3 max-w-2xl text-base text-parchment sm:text-lg">{data.message}</p>
        {data.location && <p className="mt-2 inline-flex items-center gap-2 rounded-full border border-gold/50 bg-black/15 px-3 py-1 text-sm font-semibold text-gold-bright" data-testid="odwira-location"><MapPin size={14} /> {data.location}</p>}
        <div className="mt-4 flex flex-wrap items-center gap-4"><ShareBanner title={data.title} message={data.message} path={detailsPath(data)} /><Dots count={count} index={idx} onPick={onPick} /></div>
      </div>
    </div>
  );
}

export default function OdwiraCountdown() {
  const { data: raw } = useApi("/settings/odwira");
  const { t: tr } = useLang();
  let banners = [];
  if (raw?.banners?.length) banners = raw.banners;
  else if (raw?.enabled) banners = [raw];
  const sig = banners.map((b) => b.id || b.title).join("|");
  const [idx, setIdx] = useBannerRotation(banners.length);
  const [dismissed, setDismissed] = useState(false);
  const data = banners[Math.min(idx, Math.max(0, banners.length - 1))];
  const t = useCountdown(data?.date);
  const scrolled = useScrolled(320);
  useEffect(() => { if (sig) setDismissed(sessionStorage.getItem(KEY) === sig); }, [sig]);
  if (!data || !t) return null;
  const close = () => { sessionStorage.setItem(KEY, sig); setDismissed(true); };
  const open = () => { sessionStorage.removeItem(KEY); setDismissed(false); window.scrollTo({ top: 0, behavior: "smooth" }); };
  if (dismissed) return <DrumFab count={banners.length} onOpen={open} tr={tr} />;
  return (
    <>
      <StickyBar data={data} t={t} tr={tr} visible={scrolled} />
      <section className="announce-banner relative overflow-hidden text-parchment" data-testid="odwira-banner" data-banner-index={idx}>
        <div className="kente-border" />
        <div className="announce-glow" />
        <button onClick={close} className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-gold/50 bg-black/25 text-parchment transition-colors hover:bg-gold hover:text-crimson" aria-label="Close announcement" data-testid="odwira-close"><X size={18} /></button>
        <div key={data.id || idx} className="relative mx-auto flex max-w-7xl flex-col gap-8 px-6 py-12 animate-fade-up lg:flex-row lg:items-center lg:justify-between lg:px-8 lg:py-14">
          <BannerBody data={data} tr={tr} count={banners.length} idx={idx} onPick={setIdx} />
          <div className="flex flex-col items-center gap-4 lg:items-end">
            <Timer t={t} tr={tr} />
            <Link to={detailsPath(data)} className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3 text-base font-bold text-crimson shadow-lg transition-transform hover:scale-105 hover:bg-gold-bright" data-testid="odwira-cta">{tr("Details")} <ChevronRight size={18} /></Link>
          </div>
        </div>
        <div className="kente-border" />
      </section>
    </>
  );
}
